import type { CreateTruckInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, InputNumber } from 'antd';
import { Truck } from 'lucide-react';
import type { TruckClass } from '../../../../reference-data/reference-data.api';
import { TruckCapabilityFields } from './TruckCapabilityFields';
import { TruckParticularsFields } from './TruckParticularsFields';

interface CreateTruckDrawerProps {
  open: boolean;
  truckClasses: TruckClass[];
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateTruckInput) => Promise<boolean>;
  onClose: () => void;
}

/** Registration, class, particulars, capacity and capability flags — the truck's starting odometer reading is set here once; only a reading can move it after. */
export function CreateTruckDrawer({ open, truckClasses, error, submitting, onCreate, onClose }: CreateTruckDrawerProps) {
  const [form] = Form.useForm<CreateTruckInput>();

  async function submit(values: CreateTruckInput) {
    const created = await onCreate(values);
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create truck" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateTruckInput>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ hasGenset: false, isBonded: false, isHazardousCertified: false, isCrossBorderEquipped: false, currentOdometerKm: 0 }}
      >
        <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
          <Input className="font-mono uppercase" autoFocus />
        </Form.Item>
        <TruckParticularsFields truckClasses={truckClasses} />
        <TruckCapabilityFields />
        <Form.Item name="currentOdometerKm" label="Starting odometer (km)" rules={[{ required: true, message: 'Enter the odometer reading' }]}>
          <InputNumber className="w-full" min={0} step={100} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting} icon={<Truck className="size-4" />}>
            Create truck
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
